import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, ToggleButtonGroup, ToggleButton, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, TextField,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { api } from '../../utils/api';

// Reason options must mirror PLAN_APPROVE_REASONS / PLAN_REJECT_REASONS in
// backend/src/handlers/admin/users-management.ts. The backend validates against
// these exact strings and requires a note when 'Other' is chosen.
const APPROVE_REASONS = [
  'Verified payment received',
  'Promotional / comp upgrade',
  'Control / introduction account',
  'Customer support resolution',
  'Internal / team account',
  'Other',
];
const REJECT_REASONS = [
  'Payment not verified',
  'Duplicate request',
  'Ineligible for requested plan',
  'Suspected abuse / fraud',
  'Other',
];

const TEAL = '#2D6E6A';

const PlanRequestsTab: React.FC = () => {
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('pending');
  const [processing, setProcessing] = useState<number | null>(null);
  const [success, setSuccess] = useState('');

  // Reason dialog state
  const [dialog, setDialog] = useState<{ open: boolean; requestId: number | null; action: 'approve' | 'reject' }>(
    { open: false, requestId: null, action: 'approve' }
  );
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/admin/users/plan-requests?status=${filter}`);
      const data = await res.json();
      if (data.success) {
        setRequests(data.requests || []);
      } else {
        setError(data.error || 'Failed to load requests');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const openDialog = (requestId: number, action: 'approve' | 'reject') => {
    setReason('');
    setNote('');
    setDialog({ open: true, requestId, action });
  };

  const closeDialog = () => setDialog({ open: false, requestId: null, action: 'approve' });

  const reasonOptions = dialog.action === 'approve' ? APPROVE_REASONS : REJECT_REASONS;
  const noteRequired = reason === 'Other';
  const canSubmit = !!reason && (!noteRequired || note.trim().length >= 3);

  const submit = async () => {
    if (dialog.requestId == null || !canSubmit) return;
    const { requestId, action } = dialog;
    setProcessing(requestId);
    setSuccess('');
    setError('');
    closeDialog();
    try {
      const res = await api.post('/admin/users/plan-requests', {
        requestId, action, reason, note: note.trim() || undefined,
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(`Request ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
        fetchRequests();
      } else {
        setError(data.error || `Failed to ${action}`);
      }
    } catch {
      setError(`Failed to ${dialog.action} request`);
    } finally {
      setProcessing(null);
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'pro': return TEAL;
      case 'premium': return '#7B1FA2';
      default: return '#666';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>Plan Requests</Typography>
        <ToggleButtonGroup value={filter} exclusive onChange={(_, v) => v && setFilter(v)} size="small">
          <ToggleButton value="pending">Pending</ToggleButton>
          <ToggleButton value="approved">Approved</ToggleButton>
          <ToggleButton value="rejected">Rejected</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: TEAL }} /></Box>
      ) : requests.length === 0 ? (
        <Alert severity="info">No {filter} plan requests.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Current</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Requested</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                {filter === 'pending' && <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((req) => (
                <TableRow key={req.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={req.avatarUrl} sx={{ width: 32, height: 32, bgcolor: TEAL, fontSize: '0.85rem' }}>
                        {(req.displayName || req.email || '?')[0].toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{req.displayName || 'Unknown'}</Typography>
                        <Typography variant="caption" color="text.secondary">{req.email}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell><Chip label={req.currentPlan} size="small" variant="outlined" /></TableCell>
                  <TableCell><Chip label={req.requestedPlan} size="small" sx={{ bgcolor: getPlanColor(req.requestedPlan), color: 'white', fontWeight: 600 }} /></TableCell>
                  <TableCell><Chip label={req.isDowngrade ? 'Downgrade' : 'Upgrade'} size="small" color={req.isDowngrade ? 'warning' : 'success'} variant="outlined" /></TableCell>
                  <TableCell>
                    <Typography variant="caption">{new Date(req.requestedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Typography>
                  </TableCell>
                  {filter === 'pending' && (
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button size="small" variant="contained" startIcon={processing === req.id ? <CircularProgress size={14} /> : <CheckCircleIcon />}
                          disabled={processing !== null} onClick={() => openDialog(req.id, 'approve')}
                          sx={{ bgcolor: TEAL, textTransform: 'none', borderRadius: 2, '&:hover': { bgcolor: '#1F4E4A' } }}>Approve</Button>
                        <Button size="small" variant="outlined" startIcon={<CancelIcon />}
                          disabled={processing !== null} onClick={() => openDialog(req.id, 'reject')}
                          sx={{ textTransform: 'none', borderRadius: 2, color: '#d32f2f', borderColor: '#d32f2f' }}>Reject</Button>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Reason dialog — required because the backend now validates reason on every decision */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{dialog.action === 'approve' ? 'Approve' : 'Reject'} plan request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a reason for this decision. It is recorded in the audit trail.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Reason</InputLabel>
            <Select value={reason} label="Reason" onChange={(e) => setReason(e.target.value)}>
              {reasonOptions.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
          {noteRequired && (
            <TextField
              fullWidth size="small" label="Note (required for Other)" multiline minRows={2}
              value={note} onChange={(e) => setNote(e.target.value)}
              helperText={note.trim().length < 3 ? 'Please add a short note (min 3 characters).' : ' '}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={submit} variant="contained" disabled={!canSubmit}
            sx={{ bgcolor: dialog.action === 'approve' ? TEAL : '#d32f2f', textTransform: 'none', '&:hover': { bgcolor: dialog.action === 'approve' ? '#1F4E4A' : '#b71c1c' } }}
          >
            {dialog.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlanRequestsTab;
